import { UIMode } from 'library/ts/enums/UIMode';
import { IPlayerAdapterDeviceCapabilities } from 'library/ts/player/adapter/interfaces/capabilities/IPlayerAdapterDeviceCapabilities';
import { AbstractTagClass } from 'tag/ts/tag/abstract/AbstractTagClass';
import { PauseAdService } from 'tag/ts/tag/ads/pause/PauseAdService';
import { TagNerdModeAPI } from 'tag/ts/tag/api/TagNerdModeAPI';
import { UIModeService } from 'tag/ts/tag/api/UIModeService';
import { UpdateConfigService } from 'tag/ts/tag/api/UpdateConfigService';
import { TagEvent } from 'tag/ts/tag/events/TagEvent';
import { ITagActionDataOut } from 'tag/ts/tag/interfaces/api/ITagActionDataOut';
import { ITagAPI } from 'tag/ts/tag/interfaces/api/ITagAPI';
import { ITagAPIIn } from 'tag/ts/tag/interfaces/api/ITagAPIIn';
import { ITagAPIOut } from 'tag/ts/tag/interfaces/api/ITagAPIOut';
import { IUpdateConfigPartial } from 'tag/ts/tag/interfaces/api/IUpdateConfigPartial';
import { ITagAbstractClassArgument } from 'tag/ts/tag/interfaces/arguments/ITagAbstractClassArgument';
import { IConfigUpdatedEventData } from 'tag/ts/tag/interfaces/events/IConfigUpdatedEventData';
import { ITagContextVO } from 'tag/ts/tag/interfaces/vo/ITagContextVO';
import { TPauseAdProvider } from 'tag/ts/tag/types/ads/pause/TPauseAdProvider';
import { TPauseAdResponse } from 'tag/ts/tag/types/ads/pause/TPauseAdResponse';

/**
 * @copyright © Paramount Streaming
 * @author  Stan Gletski
 * @description Tag API
 */
export class TagAPI extends AbstractTagClass implements ITagAPI {

	// Service backing tag.API.TAG.updateConfig() — owns whitelist, validation, deep-merge,
	// and ON_CONFIG_UPDATED dispatch. Constructed once per Tag instance; the IN.updateConfig
	// closure delegates straight through.
	readonly #updateConfigService: UpdateConfigService;

	// Service backing tag.API.TAG.uiMode() / getUiMode() and Skin's tag.notifyUiModeChangeDone()
	// hook. Owns current-mode state, in-flight lock, and ON_UI_MODE_CHANGE_REQUEST/DONE dispatch.
	readonly #uiModeService: UIModeService;

	// Backs tag.API.TAG.NerdMode — owns all 27 Nerd Mode API methods. Constructed once per
	// Tag instance; the IN.NerdMode getter returns this instance directly.
	readonly #nerdModeAPI: TagNerdModeAPI;

	constructor( argument: ITagAbstractClassArgument ) {
		super(
			{
				...argument,
				name: 'TagAPI'
			}
		);

		this.#updateConfigService = new UpdateConfigService( { tag: argument.tag } );
		this.#uiModeService = new UIModeService( { tag: argument.tag } );
		this.#nerdModeAPI = new TagNerdModeAPI( { tag: argument.tag } );
	}

	/**
	 * @description Skin's completion hook for `tag.API.TAG.uiMode()` transitions, routed via
	 * `tag.notifyUiModeChangeDone(mode)`. Dispatches TagEvent.ON_UI_MODE_CHANGE_DONE and
	 * releases the in-flight lock. Not intended for host code — kept off the IN namespace.
	 */
	notifyUiModeChangeDone = ( mode: UIMode ): void => {
		this.#uiModeService.notifyDone( mode );
	};

	/**
	 * @description Returns namespace from SmartTag parameters
	 * @return {ITagAPIOut}
	 */
	get namespace() {
		return this.params?.TAG?.namespace?.TAG;
	}

	/**
	 * @description Methods on page called by tag instance
	 */
	get OUT(): ITagAPIOut {
		return {
			/**
			 * @description Tag action passed to page
			 * @param {ITagActionDataOut} data
			 * @param {boolean} returnValue - configurable request to return value. true => return value, default => false
			 * @return {any}
			 */
			tagAction: ( data: ITagActionDataOut, returnValue: boolean = false ): void | any => {
				switch ( returnValue ) {
					case false:
						this.utils.API.executeCallback( 'tagAction', this.namespace, data );
						break;
					case true:
						return this.utils.API.executeCallback( 'tagAction', this.namespace, data, true ) as Promise<any> | any;
				}
			},

			/**
			 * @description Validate international context
			 * @param {ITagContextVO} context
			 * @return {boolean}
			 */
			checkContext: ( context: ITagContextVO ): boolean => {
				return this.utils.API.executeCallback( 'checkContext', this.namespace, context, true );
			},

			/**
			 * @description Sends a Nerd Mode toggle request to the Chromecast receiver via Cast SDK.
			 * Calls window.smartTagAPI.TAG.toggleCastNerdMode(level) — TN Web must implement this.
			 * @param level - Nerd Mode level (0=off, 1=basic, 2=enhanced, 3=full)
			 */
			toggleCastNerdMode: ( level: number ): void => {
				this.utils.API.executeCallback( 'toggleCastNerdMode', this.namespace, level );
			}
		};
	}

	/**
	 * @description Methods called on tag instance by page
	 */
	get IN(): ITagAPIIn {
		return {
			/**
			 * @description Resets tag application instance
			 */
			reset: (): Promise<any> => {
				this?.logger?.log( 'IN:', 'method: reset' );
				return new Promise(
					( resolve, reject ) => {
						this?.logger?.log( 'IN:', 'method:', 'reset:', 'dispatch:', TagEvent.ON_API_RESET_TAG_REQUEST );

						// dispatch application event: request to reset Tag
						this.dispatchTagEvent(
							this.getTagEventVO(
								TagEvent.ON_API_RESET_TAG_REQUEST,
								this.tag,
								{
									promiseResolve: resolve,
									promiseReject: reject
								},
								this,
								this.name
							)
						);
					}
				);
			},
			/**
			 * @description Get Pause Ad Results Object
			 * @param provider - Pause ad provider (`SmartTag.enums.PauseAdProvider.FREEWHEEL`, `.GAM`, or `.AD_IMAGE_SERVICE`).
			 * @returns Promise resolving to `TPauseAdResponse` on success, rejecting on failure or no-fill.
			 */
			getPauseAd: ( provider: TPauseAdProvider ): Promise<TPauseAdResponse> => {
				return PauseAdService.getPauseAd( this.tag, provider );
			},

			/**
			 * @description Get device capabilities
			 * @returns Promise<IPlayerAdapterDeviceCapabilities>
			 */
			getDeviceCapabilities: (): Promise<IPlayerAdapterDeviceCapabilities> => {
				// No conversion needed - using consistent IPlayerAdapterDeviceCapabilities
				return this.tag.controller?.playerManager?.playerAdapter?.getDeviceCapabilities();
			},

			/**
			 * @description Merges a whitelisted partial into tag.model.config at runtime and
			 * resolves with the diff payload. Programmer errors (bad input, Tag not initialized)
			 * throw synchronously; the Promise rejection path is reserved for future async
			 * failures. Effective on next player rebuild — see UpdateConfigService for full
			 * semantics. Fires TagEvent.ON_CONFIG_UPDATED with the same diff payload.
			 * @param partial - Whitelisted partial overrides for tag.model.config.
			 * @returns Diff payload listing changed dot-paths with per-path before/after values.
			 */
			updateConfig: ( partial: IUpdateConfigPartial ): Promise<IConfigUpdatedEventData> => {
				return this.#updateConfigService.updateConfig( partial );
			},

			/**
			 * @description Switches the active Skin overlay chrome (mini / normal / theatre) and,
			 * optionally, latches the orthogonal persistent / "always-on" host flag.
			 * Accepts `SmartTag.enums.UIMode.MINI / .NORMAL / .THEATRE`.
			 *
			 * Dispatches TagEvent.ON_UI_MODE_CHANGE_REQUEST when accepted, and ON_UI_MODE_CHANGE_DONE
			 * once Skin's MiniPlayerManager finishes the overlay swap (or synchronously when no Skin
			 * is attached).
			 *
			 * Semantics:
			 *  - Invalid mode → warning + no-op (the `alwaysOn` flag, if supplied, is still applied).
			 *  - Idempotent (already in target mode) → no-op, no REQUEST (the `alwaysOn` flag, if supplied, is still applied).
			 *  - Re-entrancy blocked while a transition is in flight (REQUEST → DONE pending) → warning + no-op.
			 *
			 * @param mode - Target UI chrome mode.
			 * @param alwaysOn - Optional persistent-host flag. `true`/`false` sets/clears it; omit to leave
			 *   it unchanged. Read back via {@link getIsAlwaysOn}; suppresses the end-card full-frame
			 *   homepage redirect so a reused-instance host is not torn down.
			 */
			uiMode: ( mode: UIMode, alwaysOn?: boolean ): void => {
				this.#uiModeService.requestMode( mode, alwaysOn );
			},

			/**
			 * @description Returns the current UI mode (Skin-owned state mirrored on Tag).
			 * Defaults to `UIMode.NORMAL` before any successful transition.
			 */
			getUiMode: (): UIMode => {
				return this.#uiModeService.currentMode;
			},

			/**
			 * @description Returns the persistent / "always-on" host flag last set via
			 * `uiMode(mode, alwaysOn)`. Defaults to `false`. Orthogonal to `getUiMode()`.
			 */
			getIsAlwaysOn: (): boolean => {
				return this.#uiModeService.isAlwaysOn;
			},

			/**
			 * @description Loading animation public API — delegates to the internal LoadingAnimationManager.
			 * Returns undefined if TagView has not yet initialized.
			 */
			LoadingAnimation: this.tag.view?.loadingAnimationManager,

			/**
			 * @description Nerd Mode diagnostic overlay namespace — see {@link ITagAPINerdMode}
			 * / {@link TagNerdModeAPI} for the full method-by-method implementation.
			 */
			NerdMode: this.#nerdModeAPI
		};
	}

}